import type {
  ControlledFieldWrapProps,
  UseFormReturn,
} from '@leafwell/components';
import React, { useState } from 'react';
import {
  useToast,
  Button,
  Card,
  FileFieldControlled,
  Form,
  Heading,
  Paragraph,
  TextFieldControlled,
  Wrap,
} from '@leafwell/components';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { SelectFieldControlled } from './select-field';
import { restBaseUrl, restHeaders } from 'lib/rest';

type UserReview = {
  author: string;
  email?: string;
  location?: string;
  content: string;
  rating: number;
  photo: File | null;
};

const ReviewForm: React.FC = () => {
  const { translations } = useWordPressContext();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  async function addReview(
    { author, email, location, content, rating, photo }: UserReview,
    form: UseFormReturn,
  ) {
    setIsLoading(true);
    let featuredMediaId;

    if (photo) {
      const res = await fetch(`${restBaseUrl}/media`, {
        method: 'POST',
        headers: {
          ...restHeaders,
          'Content-Disposition': `attachment; filename=${photo.name}`,
          'Content-Type': photo.type,
        },
        body: photo,
      });

      if (res.ok) {
        const data = await res.json();
        featuredMediaId = data?.id ?? '';
      } else {
        addToast({
          content: translations['An error occurred, please try again.'],
          key: 'user-review-error',
          type: 'negative',
          shouldRemove: true,
        });
      }
    }

    const review = {
      title: `User review: ${author}`,
      content,
      acf: {
        rating,
        review_author: {
          name: author,
          location,
        },
      },
      status: 'private',
    };

    // If email is not present do not add it cause it'll trigger an error from REST API
    if (email) {
      review.acf.review_author['email'] = email;
    }

    if (featuredMediaId) {
      review['featured_media'] = featuredMediaId;
    }

    const res = await fetch(`${restBaseUrl}/review`, {
      method: 'POST',
      headers: restHeaders,
      body: JSON.stringify(review),
    });

    if (res.ok) {
      addToast({
        content: translations['Your review has been submitted.'],
        key: 'user-review-success',
        type: 'positive',
        shouldRemove: true,
      });
      // Reset form on success to initial values
      form.reset();
    } else {
      addToast({
        content:
          //translations[error?.message] ||
          translations['An error occurred, please try again.'],
        key: 'user-review-error',
        type: 'negative',
        shouldRemove: true,
      });
    }
    setIsLoading(false);
  }

  const formArgs = {
    labelVisible: false,
    theme: 'dark' as ControlledFieldWrapProps['theme'],
  };

  return (
    <Wrap size="large" id="review-form">
      <Card
        filled
        rounded
        size="large"
        variant="light"
        className="col-span-full"
      >
        <header className="text-center">
          <Heading level="2">{translations['Add review']}</Heading>
          <Paragraph size="large">
            {translations['Your review is subject to moderation']}
          </Paragraph>
        </header>
        <Form
          className="justify-self-center max-w-4xl sm:grid-cols-2 gap-y-3 sm:gap-x-12"
          defaultValues={{
            author: '',
            email: '',
            location: '',
            content: '',
            rating: '',
            photo: null,
          }}
          onSubmit={({ photo, ...values }, form) => {
            addReview({ photo: photo?.[0], ...values } as UserReview, form);
          }}
        >
          <div className="grid gap-y-3 items-start">
            <SelectFieldControlled
              label={translations['Rating']}
              name="rating"
              placeholder={`${translations['Rating']} *`}
              options={[1, 2, 3, 4, 5].map(value => ({
                label: value.toString(),
                value,
              }))}
              validations={['required']}
              {...formArgs}
            />
            <TextFieldControlled
              label={translations['Name']}
              name="author"
              placeholder={`${translations['Name']} *`}
              validations={['required']}
              {...formArgs}
            />
            <TextFieldControlled
              label={translations['Email']}
              name="email"
              placeholder={translations['Email']}
              type="email"
              {...formArgs}
            />
            <TextFieldControlled
              label={translations['Location']}
              name="location"
              placeholder={translations['Location']}
              {...formArgs}
            />
            <TextFieldControlled
              label={translations['Message']}
              name="content"
              placeholder={`${translations['Message']} *`}
              type="textarea"
              maxLength={500}
              {...formArgs}
            />
          </div>
          <div className="grid gap-y-3 lg:gap-y-6 content-between">
            <FileFieldControlled
              accept={['image/*', 'capture=camera']}
              label={translations['Photo']}
              name="photo"
              helpText={
                translations[
                  'Allowed file types: image/png,image/jpg,image/jpeg. Max size: 2MB.'
                ]
              }
              maxSize={2}
              {...formArgs}
            />
            <Button
              label={translations['Submit review']}
              rounded={true}
              type="submit"
              loading={isLoading}
              variant="dark"
              disabled={isLoading}
            />
          </div>
        </Form>
      </Card>
    </Wrap>
  );
};

export default ReviewForm;
